
import { mapState, mapGetters, mapActions } from "vuex";
import { composeGid } from "@shopify/admin-graphql-api-utilities";
import { Navigation } from "swiper";
import { getPriorityCampaign, getMaxDiscount } from "~/utils/campaign-utils";
import { getPrismicTime } from "~/utils/prismic-utils";
import { delay } from "~/utils";

import ShopifyProductTile from "~/components/common/AppShopifyProductVariantSelectorTile";

export default {
  components: { ShopifyProductTile },

  props: {
    products: { type: Array, required: true },
    discount: { type: Number, default: 0 },
    qualifyingTags: { type: Array, default: () => [] },
    /** Prismic "PDP price callout" (e.g. Bundle & Save 5%) — also used for bundle CTA label. */
    pdpCallout: { type: String, default: "" },
  },

  data() {
    return {
      selectedVariants: {},
      currentVariantByProductId: {},
      confirmedSelectionsByProductId: {},
      variantPriceData: {},
      loading: false,
      remainingSwiper: null,
      swiperIsBeginning: true,
      swiperIsEnd: false,
      swiperOptions: {
        slidesPerView: 1.15,
        spaceBetween: 0,
        loop: false,
        breakpoints: {
          801: {
            slidesPerView: 2,
          },
        },
        loopFillGroupWithBlank: false,
        simulateTouch: false,
        touchStartPreventDefault: false,
        preventClicks: false,
        preventClicksPropagation: false,
        modules: [Navigation],
        navigation: {
          nextEl: ".bundle-remaining-swiper-next",
          prevEl: ".bundle-remaining-swiper-prev",
        },
      },
    };
  },

  computed: {
    ...mapState("bag", ["miniCart"]),
    ...mapGetters("bag", ["snoozeMemberAttrs", "velocityAttrs", "lines"]),

    normalizeLineItems() {
      // Keyed by Shopify GID (lineItem.variant.variantId). We use a Map because downstream code expects `.get()`.
      const lines = Array.isArray(this.lines) ? this.lines : [];
      const lineItemsByVariantId = new Map();

      for (const lineItem of lines) {
        const variantGid =
          lineItem?.variant?.variantId ??
          (lineItem?.variant?.id
            ? composeGid(
                "ProductVariant",
                this.normalizeVariantId(lineItem.variant.id)
              )
            : null);

        if (!variantGid) continue;

        const existing = lineItemsByVariantId.get(variantGid);
        if (!existing) {
          lineItemsByVariantId.set(variantGid, lineItem);
          continue;
        }

        // Defensive merge: if somehow the cart contains multiple lines for the same variant,
        // preserve a single entry but aggregate quantity and keep existing attributes (e.g. _time).
        const existingQuantity = Number(existing?.quantity) || 0;
        const newQuantity = Number(lineItem?.quantity) || 0;

        lineItemsByVariantId.set(variantGid, {
          ...existing,
          quantity: existingQuantity + newQuantity,
          attributes: existing?.attributes ?? lineItem?.attributes ?? [],
        });
      }

      return lineItemsByVariantId;
    },
    remainingProducts() {
      return (this.products || []).slice(1);
    },
    totalSelectedItems() {
      return this.selectedVariants
        ? Object.keys(this.selectedVariants).length
        : 0;
    },
    calculatedBundleTotal() {
      return Object.keys(this.selectedVariants).reduce((total, productId) => {
        const variantId = this.selectedVariants[productId];
        const { price, compareAtPrice } = this.getVariantPriceDetails(
          productId,
          variantId
        );
        const product = this.findProductById(productId);
        const effectivePrice = this.getProductEffectivePrice(
          price,
          compareAtPrice,
          product?.tags
        );
        return total + effectivePrice;
      }, 0);
    },
    calculatedBundleCompareAtTotal() {
      return Object.keys(this.selectedVariants).reduce((total, productId) => {
        const variantId = this.selectedVariants[productId];
        const { price, compareAtPrice } = this.getVariantPriceDetails(
          productId,
          variantId
        );
        return (
          total +
          (Number(compareAtPrice) > 0 ? Number(compareAtPrice) : Number(price))
        );
      }, 0);
    },
    bundleSavings() {
      const totalComparePrice =
        Number(this.calculatedBundleCompareAtTotal) || 0;
      const cartSubTotal = Number(this.calculatedBundleTotal) || 0;
      const totalSaving = totalComparePrice - cartSubTotal;
      return totalSaving > 0 ? totalSaving : 0;
    },
    bundleHasQualifyingProduct() {
      const tags = this.qualifyingTags || [];
      if (!tags.length) return false;
      return Object.keys(this.selectedVariants).some(productId => {
        const product = this.findProductById(productId);
        return product?.tags?.some(t => tags.includes(t));
      });
    },
    bundleEligibleForDiscount() {
      return (
        this.totalSelectedItems >= 2 &&
        this.bundleHasQualifyingProduct &&
        Number(this.discount) > 0
      );
    },
    bundleDiscountSaving() {
      if (!this.bundleEligibleForDiscount) return 0;
      const total = Number(this.calculatedBundleTotal) || 0;
      return total * (Number(this.discount) / 100);
    },
    totalBundleSavings() {
      return (
        (Number(this.bundleSavings) || 0) +
        (Number(this.bundleDiscountSaving) || 0)
      );
    },
    bundleDiscountCalloutLabel() {
      const fromCms = this.pdpCallout?.trim();
      if (fromCms) return fromCms;
      return this.discount > 0
        ? `Bundle & Save ${this.discount}%`
        : "Bundle & Save";
    },
    activeCampaigns() {
      const now = Date.now();
      return (this.$store.state.campaignSetting?.campaign_setting ?? []).filter(
        ({ startTime, endTime, enableCampaign }) =>
          enableCampaign &&
          now >= getPrismicTime(startTime) &&
          now <= getPrismicTime(endTime)
      );
    },
    bundlePriorityCampaign() {
      const activeCampaigns = this.activeCampaigns;
      for (const productId of Object.keys(this.selectedVariants)) {
        const product = this.findProductById(productId);
        const campaign = getPriorityCampaign(product?.tags ?? [])(
          activeCampaigns
        );
        if (campaign) return campaign;
      }
      return null;
    },
    isMemberCampaign() {
      const title = this.bundlePriorityCampaign?.campaignTitle ?? "";
      return title === "member-campaign" || title === "velocity-campaign";
    },
    memberCampaignColor() {
      return this.bundlePriorityCampaign?.saleColor ?? "";
    },
    memberCampaignLabel() {
      return this.bundlePriorityCampaign?.saleTileTitle ?? "";
    },
    rrpBundleAfterDiscount() {
      const rrp = Number(this.calculatedBundleCompareAtTotal) || 0;
      if (!this.bundleEligibleForDiscount) return rrp;
      return Math.max(0, rrp - rrp * (Number(this.discount) / 100));
    },
    /** Total shown under "Total Bundle Price" (subtotal minus campaign % when eligible). */
    displayBundleTotal() {
      if (!this.bundleEligibleForDiscount) {
        return Number(this.calculatedBundleTotal) || 0;
      }
      const subtotal = Number(this.calculatedBundleTotal) || 0;
      return Math.max(0, subtotal - this.bundleDiscountSaving);
    },
    bundlePayload() {
      const totalItems = Object.keys(this.selectedVariants)
        .map(productId => {
          const variantId = this.selectedVariants[productId];
          const product = this.findProductById(productId);
          const variant = this.findVariantById(product, variantId);

          if (!variant) return null;

          const { compareAtPrice: compareAtPriceAmount } =
            this.getVariantPriceDetails(productId, variantId);

          let compareAtPriceCents = (Number(compareAtPriceAmount) || 0) * 100;
          compareAtPriceCents = Number.isNaN(compareAtPriceCents)
            ? 0
            : compareAtPriceCents;

          const pixelDetails = JSON.stringify({
            brand: product?.vendor ?? "",
            category: product?.productType ?? "",
          });

          return {
            variantId: composeGid(
              "ProductVariant",
              this.normalizeVariantId(variantId)
            ),
            quantity: 1,
            attributes: [
              { key: "_pixel_details", value: pixelDetails },
              { key: "_compare_at_price", value: String(compareAtPriceCents) },
              { key: "_time", value: this.getTime(variantId) },
            ],
          };
        })
        .filter(Boolean);

      let addPayload = [];
      let updatePayload = [];

      for (const { variantId, quantity, attributes } of totalItems) {
        const lineItem = this.normalizeLineItems.get(variantId);

        const payload = {
          attributes: [
            ...attributes,
            this.snoozeMemberAttrs,
            this.velocityAttrs,
          ].filter(Boolean),
          merchandiseId: variantId,
          quantity: (lineItem?.quantity ?? 0) + quantity,
        };

        lineItem
          ? updatePayload.push({ ...payload, id: lineItem.id })
          : addPayload.push(payload);
      }

      return { addPayload, updatePayload };
    },
  },

  watch: {
    products: {
      immediate: true,
      async handler() {
        this.initSelectedVariants();
        if (!this.$el) return;
        this.destroyRemainingSwiper();
        await this.initRemainingSwiper();
      },
    },
    "$route.query.variant": {
      immediate: true,
      handler(newVariantId) {
        const firstProduct = this.products?.[0];
        if (!firstProduct?.id || !newVariantId) return;

        const normalizedQueryId = this.normalizeVariantId(newVariantId);
        const matched =
          firstProduct?.variants?.find(
            v => this.normalizeVariantId(v?.id) === normalizedQueryId
          ) ?? null;

        this.$set(
          this.currentVariantByProductId,
          firstProduct.id,
          this.normalizeVariantId(matched?.id ?? newVariantId)
        );
      },
    },
  },

  async mounted() {
    await this.initRemainingSwiper();
  },

  destroyed() {
    this.destroyRemainingSwiper();
  },

  methods: {
    ...mapActions("recommendations", ["trackBeacon"]),
    async handleNavClick(direction) {
      const source = this.products[0]?.source;
      if (!source?.includes("searchspring-")) return;
      const [, tag] = source.split("searchspring-");

      try {
        if (direction === "next") {
          await delay(250);
          await this.trackBeacon({ type: "clickImpression", tag });
        } else {
          await this.trackBeacon({ type: "click", tag });
        }
      } catch (error) {
        this.$bugsnag.notify(error, e => {
          e.context = `ProductBundleList handleNavClick ${direction}`;
        });
      }
    },
    async initRemainingSwiper() {
      if (!this.remainingProducts.length) return;
      await this.$nextTick();
      let swiper = await this.$swiper.init(
        ".swiper.bundle-remaining-products-swiper",
        this.swiperOptions
      );
      // Swiper 9 returns an array when the selector matches multiple DOM nodes.
      if (Array.isArray(swiper)) {
        swiper = swiper[0] ?? null;
      }
      this.remainingSwiper = swiper;
      if (this.remainingSwiper) {
        this.swiperIsBeginning = this.remainingSwiper.isBeginning;
        this.swiperIsEnd = this.remainingSwiper.isEnd;
        this.remainingSwiper.on("slideChange", this.onSwiperSlideChange);
        this.remainingSwiper.on("reachBeginning", this.onSwiperSlideChange);
        this.remainingSwiper.on("reachEnd", this.onSwiperSlideChange);
      }
    },
    destroyRemainingSwiper() {
      const swiper = this.remainingSwiper;
      if (swiper && typeof swiper.off === "function") {
        swiper.off("slideChange", this.onSwiperSlideChange);
        swiper.off("reachBeginning", this.onSwiperSlideChange);
        swiper.off("reachEnd", this.onSwiperSlideChange);
      }
      this.$swiper.destroy(".swiper.bundle-remaining-products-swiper");
      this.remainingSwiper = null;
      this.swiperIsBeginning = false;
      this.swiperIsEnd = false;
    },
    onSwiperSlideChange() {
      if (!this.remainingSwiper) return;
      this.swiperIsBeginning = this.remainingSwiper.isBeginning;
      this.swiperIsEnd = this.remainingSwiper.isEnd;
    },
    mobileSlide(direction) {
      if (!this.remainingSwiper) return;
      if (direction < 0) {
        this.remainingSwiper.slidePrev();
        this.handleNavClick("prev");
      } else {
        this.remainingSwiper.slideNext();
        this.handleNavClick("next");
      }
    },
    getProductEffectivePrice(price, compareAtPrice, productTags) {
      const campaign = getPriorityCampaign(productTags ?? [])(
        this.activeCampaigns
      );
      if (!campaign) return Number(price) || 0;

      const tagsMaxDiscount = getMaxDiscount(productTags ?? [])(
        campaign.tags ?? ""
      );
      const maxDiscount =
        tagsMaxDiscount === 0 ? campaign.salePercentage ?? 0 : tagsMaxDiscount;

      const p = Number(price) || 0;
      const cap = Number(compareAtPrice) || 0;
      const campaignTitle = campaign.campaignTitle ?? "";

      switch (campaignTitle) {
        case "was-now":
          return p;

        case "member-campaign":
        case "velocity-campaign": {
          if (!maxDiscount) return p;
          const rrp = Number(cap) && p < cap ? cap : p;
          const discounted = rrp - (rrp * maxDiscount) / 100;
          return p > discounted ? discounted : p;
        }

        default: {
          if (!maxDiscount) return p;
          return p - (p * maxDiscount) / 100;
        }
      }
    },
    normalizeVariantId(id) {
      if (id == null) return id;
      const s = String(id);
      if (s.includes("/")) {
        return s.split("/").slice(-1)[0];
      }
      return s;
    },
    initSelectedVariants() {
      this.selectedVariants = {};
      this.currentVariantByProductId = {};
      this.confirmedSelectionsByProductId = {};
      this.variantPriceData = {};

      if (!this.products?.length) return;

      for (const product of this.products) {
        if (!product?.id) continue;
        const initialVariant =
          product?.selectedVariant ?? product?.variants?.[0] ?? null;
        if (initialVariant?.id) {
          this.$set(
            this.currentVariantByProductId,
            product.id,
            this.normalizeVariantId(initialVariant.id)
          );
        }
      }
    },
    getTime(variantId) {
      const gid = String(variantId ?? "").includes("gid://")
        ? variantId
        : composeGid("ProductVariant", this.normalizeVariantId(variantId));

      const attributes = this.normalizeLineItems.get(gid)?.attributes ?? [];

      return (
        attributes.find(attr => attr.key === "_time")?.value ??
        String(new Date().getTime())
      );
    },
    findProductById(productId) {
      return this.products.find(
        product =>
          product.id === productId ||
          product.id.toString() === productId.toString()
      );
    },
    findVariantById(product, variantId) {
      if (!product || !product.variants) return null;

      const target = this.normalizeVariantId(variantId);
      return product.variants.find(variant => {
        const current = this.normalizeVariantId(variant?.id);
        return (
          current != null &&
          target != null &&
          current.toString() === target.toString()
        );
      });
    },
    handleChecked({ variantId, isChecked, confirmed }, productId) {
      if (variantId) {
        this.$set(
          this.currentVariantByProductId,
          productId,
          this.normalizeVariantId(variantId)
        );
      }

      if (isChecked) {
        this.$set(
          this.selectedVariants,
          productId,
          this.normalizeVariantId(variantId)
        );

        const product = this.findProductById(productId);
        const isSingleVariant = (Number(product?.variants?.length) || 0) <= 1;
        if (confirmed || isSingleVariant) {
          this.$set(this.confirmedSelectionsByProductId, productId, true);
        }
      } else {
        this.$delete(this.selectedVariants, productId);
        this.$delete(this.confirmedSelectionsByProductId, productId);
      }
    },

    handleVariantChange(variantId, productId) {
      this.$set(
        this.currentVariantByProductId,
        productId,
        this.normalizeVariantId(variantId)
      );
      if (this.selectedVariants?.[productId]) {
        this.$set(
          this.selectedVariants,
          productId,
          this.normalizeVariantId(variantId)
        );
      }
    },
    getVariantPriceDetails(productId, variantId) {
      const key = this.normalizeVariantId(variantId);
      const priceUpdate = this.variantPriceData[key];
      if (priceUpdate) {
        return {
          price: Number(priceUpdate.price) || 0,
          compareAtPrice: Number(priceUpdate.compareAtPrice) || 0,
        };
      }

      const product = this.findProductById(productId);
      const variant = product ? this.findVariantById(product, key) : null;
      if (variant) {
        return {
          price: Number(variant.price) || 0,
          compareAtPrice: Number(variant.compareAtPrice) || 0,
        };
      }

      return { price: 0, compareAtPrice: 0 };
    },
    handlePriceUpdate({ variantId, price, compareAtPrice }) {
      const key = this.normalizeVariantId(variantId);
      this.$set(this.variantPriceData, key, {
        price: Number(price) || 0,
        compareAtPrice: Number(compareAtPrice) || 0,
      });
    },
    async addBundleToBag() {
      const { addPayload, updatePayload } = this.bundlePayload;
      if (!addPayload.length && !updatePayload.length) return;

      this.loading = true;
      try {
        if (!this.miniCart.id) {
          await this.$store.dispatch("bag/CREATE_CART", addPayload);

          const cartId =
            this.miniCart?.id ?? this.$store?.state?.bag?.cart?.id ?? null;
          if (cartId && this.$cart?.setCartId) {
            this.$cart.setCartId(cartId);
          }

          const createdLineCount =
            this.$store?.state?.bag?.cart?.lines?.length ?? 0;
          if (addPayload.length && createdLineCount === 0) {
            await this.$store.dispatch("bag/ADD_CART_LINES", addPayload);
          }
        } else {
          if (updatePayload.length) {
            await this.$store.dispatch("bag/UPDATE_CART_LINES", updatePayload);
          }

          if (addPayload.length) {
            await this.$store.dispatch("bag/ADD_CART_LINES", addPayload);
          }
        }

        const variantIds = Object.values(this.selectedVariants || {});
        if (variantIds.length) {
          this.$store.dispatch("app/openAddedToBag", {
            items: variantIds.map(id =>
              composeGid("ProductVariant", this.normalizeVariantId(id))
            ),
            isTablet: false,
          });
        }
      } catch (err) {
        this.$bugsnag.notify(err);
      } finally {
        this.loading = false;
      }
    },
  },
};
